import React from 'react';
import { Box, Typography, List, ListItem, ListItemText, Divider, Link } from '@mui/material';
import { MusicNote, SpotifyLogo, YoutubeLogo } from '@phosphor-icons/react';
import useThemeStyles from '../../../../theme/useThemeStyles';

const MusicSection = ({ music }) => {
  const styles = useThemeStyles();

  const getCategoryLabel = (category) => {
    const labels = {
      processional: 'Processional',
      recessional: 'Recessional',
      firstDance: 'First Dance',
      fatherDaughterDance: 'Father-Daughter Dance',
      motherSonDance: 'Mother-Son Dance',
      dinner: 'Dinner Music',
      evening: 'Evening Music',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const musicByCategory = music?.selections?.reduce((acc, selection) => {
    if (!acc[selection.category]) {
      acc[selection.category] = [];
    }
    acc[selection.category].push(selection);
    return acc;
  }, {}) || {};

  const playlistsByCategory = music?.playlists?.reduce((acc, playlist) => {
    acc[playlist.category] = playlist;
    return acc;
  }, {}) || {};

  const categories = [...new Set([
    ...Object.keys(musicByCategory),
    ...Object.keys(playlistsByCategory)
  ])];

  if (!music || (!music.selections?.length && !music.playlists?.length)) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MusicNote size={24} style={{ marginRight: 12, color: '#64748b' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
            Music
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
            No music selections added yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MusicNote size={24} style={{ marginRight: 12, color: '#64748b' }} />
        <Typography variant="h5" sx={{ fontWeight: 600, ...styles.text.primary }}>
          Music Selections
        </Typography>
      </Box>

      {categories.map((category, catIndex) => (
        <Box key={category} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, ...styles.text.primary }}>
            {getCategoryLabel(category)}
          </Typography>

          {/* Playlist for this category */}
          {playlistsByCategory[category] && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <SpotifyLogo size={20} weight="fill" color="#1DB954" />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {playlistsByCategory[category].name}
                </Typography>
              </Box>
              {playlistsByCategory[category].description && (
                <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 1 }}>
                  {playlistsByCategory[category].description}
                </Typography>
              )}
              {playlistsByCategory[category].trackCount > 0 && (
                <Typography variant="caption" sx={{ ...styles.text.secondary }}>
                  {playlistsByCategory[category].trackCount} tracks
                </Typography>
              )}
              {playlistsByCategory[category].spotifyPlaylistUrl && (
                <Link
                  href={playlistsByCategory[category].spotifyPlaylistUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'inline-block', mt: 1 }}
                >
                  Open in Spotify
                </Link>
              )}
            </Box>
          )}

          {/* Individual selections */}
          {musicByCategory[category] && musicByCategory[category].length > 0 && (
            <List>
              {musicByCategory[category].map((selection, index) => (
                <React.Fragment key={selection._id || index}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {selection.title}
                          </Typography>
                          <Typography variant="body2" sx={{ ...styles.text.secondary }}>
                            by {selection.artist}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          {selection.notes && (
                            <Typography variant="body2" sx={{ ...styles.text.secondary, mb: 1 }}>
                              {selection.notes}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {selection.spotifyLink && (
                              <Link
                                href={selection.spotifyLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <SpotifyLogo size={16} weight="fill" color="#1DB954" />
                                Spotify
                              </Link>
                            )}
                            {selection.youtubeLink && (
                              <Link
                                href={selection.youtubeLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <YoutubeLogo size={16} weight="fill" color="#FF0000" />
                                YouTube
                              </Link>
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < musicByCategory[category].length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}

          {catIndex < categories.length - 1 && <Divider sx={{ my: 3 }} />}
        </Box>
      ))}
    </Box>
  );
};

export default MusicSection;

